import { ElButton, ElMessageBox } from 'element-plus';
import type { ElMessageBoxOptions } from 'element-plus';
import { h, type VNode } from 'vue';

import {
  DRAWER_MESSAGE_BOX_MODAL_CLASS,
  drawerMessageBoxZIndex,
} from '../components/workspace/drawer-overlay-z-index.js';

/** Drawer/폼 편집 종료 확인 — 저장·저장 안 함·취소(계속 편집) */
export type ConfirmEditCloseResult = 'save' | 'discard' | 'abort';

type AxMessageBoxRuntimeOptions = ElMessageBoxOptions & {
  zIndex?: number;
};

export interface AxConfirmDialogOptions extends AxMessageBoxRuntimeOptions {
  /** Drawer/중첩 overlay 위에 확인창을 띄워야 하는 경우 사용한다. */
  useDrawerOverlay?: boolean;
  drawerZIndex?: number;
}

function mergeModalClass(current: ElMessageBoxOptions['modalClass'], next: string): string {
  if (typeof current === 'string' && current.trim().length > 0) {
    return current.split(/\s+/).includes(next) ? current : `${current} ${next}`;
  }
  return next;
}

export function buildAxConfirmDialogOptions(
  options: AxConfirmDialogOptions = {},
): AxMessageBoxRuntimeOptions {
  const { useDrawerOverlay, drawerZIndex, ...messageBoxOptions } = options;
  if (!useDrawerOverlay) {
    return messageBoxOptions;
  }

  return {
    ...messageBoxOptions,
    zIndex: drawerMessageBoxZIndex(drawerZIndex),
    modalClass: mergeModalClass(messageBoxOptions.modalClass, DRAWER_MESSAGE_BOX_MODAL_CLASS),
  };
}

export function confirmAction(
  message: string,
  title: string,
  options: AxConfirmDialogOptions = {},
): ReturnType<typeof ElMessageBox.confirm> {
  return ElMessageBox.confirm(message, title, buildAxConfirmDialogOptions(options));
}

export interface ConfirmEditCloseOptions extends AxConfirmDialogOptions {
  message?: string;
  title?: string;
}

/** 본문 문구 + 좌측 하단 「취소」(우측 버튼은 EP 기본 __btns) */
function buildEditCloseMessage(
  text: string,
  handlers: { confirm: () => void; cancel: () => void; close: () => void },
): VNode {
  return h('div', { class: 'ax-confirm-edit-close' }, [
    h('p', { class: 'ax-confirm-edit-close__text' }, text),
    h(
      ElButton,
      { class: 'ax-confirm-edit-close__abort', size: 'default', onClick: handlers.close },
      () => '취소',
    ),
  ]);
}

/**
 * 편집 종료 확인 — **저장** / **저장 안 함** / **취소**(Drawer 유지).
 * X·ESC·취소는 `abort`, 저장 안 함은 `discard`, 저장은 `save`.
 */
export async function confirmEditClose(
  options: ConfirmEditCloseOptions = {},
): Promise<ConfirmEditCloseResult> {
  const {
    message = '변경한 내용을 저장할까요?',
    title = '편집 종료',
    useDrawerOverlay,
    drawerZIndex,
    ...rest
  } = options;

  const runtime = buildAxConfirmDialogOptions({
    ...rest,
    ...(useDrawerOverlay !== undefined ? { useDrawerOverlay } : {}),
    ...(drawerZIndex !== undefined ? { drawerZIndex } : {}),
    title,
    // ElMessageBox 는 런타임에 message 렌더 함수로 action 핸들러를 넘기지만 타입엔 그 시그니처가 없다.
    // (as unknown as 로 브리지 — no-unnecessary-type-assertion 회피, CI/로컬 타입 해상도 차이에도 안정)
    message: ((handlers: { confirm: () => void; cancel: () => void; close: () => void }) =>
      buildEditCloseMessage(message, handlers)) as unknown as NonNullable<
      ElMessageBoxOptions['message']
    >,
    showConfirmButton: true,
    confirmButtonText: '저장',
    showCancelButton: true,
    cancelButtonText: '저장 안 함',
    distinguishCancelAndClose: true,
    closeOnClickModal: false,
    customClass: ['ax-confirm-edit-close-box', rest.customClass].filter(Boolean).join(' ').trim(),
  });

  try {
    await ElMessageBox(runtime);
    return 'save';
  } catch (action: unknown) {
    if (action === 'cancel') return 'discard';
    return 'abort';
  }
}
